import { useState, useEffect } from "react";
import { PackageSearch, Circle, CheckCircle2, Clock, Truck, XCircle } from "lucide-react";
import api, { inr } from "@/lib/api";
import { toast } from "sonner";

const TIMELINE = [
  { key: "placed", label: "Order Placed", icon: CheckCircle2 },
  { key: "confirmed", label: "Confirmed", icon: Clock },
  { key: "shipped", label: "Shipped", icon: Truck },
  { key: "delivered", label: "Delivered", icon: CheckCircle2 },
];

// Map backend statuses to a visual step index
const STATUS_STEP = {
  pending: 0,
  cod_pending: 0,
  paid: 1,
  shipped: 2,
  delivered: 3,
  cancelled: -1,
};

const STATUS_LABEL = {
  pending: "Awaiting payment confirmation",
  cod_pending: "Awaiting delivery scheduling",
  paid: "Confirmed — being prepared",
  shipped: "On its way",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

export default function TrackOrderPage() {
  const [orderId, setOrderId] = useState("");
  const [phone4, setPhone4] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Auto-fill Order ID from the ?order=<id> query param (used by email links)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const q = params.get("order");
      if (q) setOrderId(q);
    } catch (_e) { /* no-op */ }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!orderId.trim() || !/^\d{4}$/.test(phone4)) {
      return toast.error("Enter your Order ID and the last 4 digits of your phone.");
    }
    setLoading(true);
    setResult(null);
    try {
      const r = await api.post("/orders/track", { order_id: orderId.trim(), phone_last4: phone4 });
      setResult(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not find that order.");
    } finally {
      setLoading(false);
    }
  };

  const activeIdx = result ? STATUS_STEP[result.status] ?? 0 : -1;
  const isCancelled = result?.status === "cancelled";

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-12 py-16">
      <div className="flex items-center gap-3">
        <PackageSearch size={28} className="text-[#8B5A2B]" strokeWidth={1.5} />
        <p className="eyebrow">Order Tracking</p>
      </div>
      <h1 className="font-serif text-4xl md:text-5xl mt-2 tracking-tight">Where is my order?</h1>
      <p className="mt-3 text-stone-600 max-w-2xl">
        Enter your Order ID and the last 4 digits of the phone number you used at checkout. No login required.
      </p>

      <form onSubmit={submit} className="mt-10 grid md:grid-cols-3 gap-4 items-end">
        <div className="md:col-span-2">
          <label className="text-xs tracking-widest uppercase text-stone-500">Order ID</label>
          <input
            data-testid="track-order-id"
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            placeholder="e.g. 692305a2-366c-4523-83bb-329282d59195"
            className="mt-1 w-full font-mono text-sm"
            required
          />
        </div>
        <div>
          <label className="text-xs tracking-widest uppercase text-stone-500">Last 4 of Phone</label>
          <input
            data-testid="track-phone-last4"
            value={phone4}
            onChange={(e) => setPhone4(e.target.value.replace(/\D/g, "").slice(0, 4))}
            placeholder="1234"
            maxLength={4}
            inputMode="numeric"
            className="mt-1 w-full"
            required
          />
        </div>
        <div className="md:col-span-3">
          <button data-testid="track-submit" disabled={loading} type="submit" className="btn-primary disabled:opacity-60">
            {loading ? "Searching…" : "Track Order"}
          </button>
        </div>
      </form>

      {result && (
        <div data-testid="track-result" className="mt-14 border border-stone-200 bg-white p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <p className="text-xs tracking-widest uppercase text-stone-500">Order</p>
              <p className="font-mono text-sm mt-1">{result.id}</p>
              <p className="mt-2 text-sm text-stone-600">
                Placed on {new Date(result.created_at).toLocaleString("en-IN")} · {result.payment_method === "cod" ? "Cash on Delivery" : "Paid Online"}
              </p>
              <p className="mt-1 text-sm text-stone-600">Deliver to: {result.shipping_city} - {result.shipping_pincode}</p>
            </div>
            <div className="text-right">
              <p className="text-xs tracking-widest uppercase text-stone-500">Total</p>
              <p className="font-serif text-2xl mt-1">{inr(result.total)}</p>
              {result.delivery_charge ? <p className="text-xs text-stone-500">incl. ₹{result.delivery_charge} delivery</p> : null}
            </div>
          </div>

          {isCancelled ? (
            <div className="mt-8 flex items-center gap-3 p-4 border border-red-200 bg-red-50 text-red-800">
              <XCircle size={20} strokeWidth={1.6} />
              <div>
                <p className="font-medium">This order was cancelled.</p>
                <p className="text-sm">If this is unexpected, please contact us on WhatsApp: +91 91158 00091.</p>
              </div>
            </div>
          ) : (
            <div className="mt-10">
              <p className="text-xs tracking-widest uppercase text-stone-500 mb-4">Status: {STATUS_LABEL[result.status] || result.status}</p>
              <ol className="relative">
                {TIMELINE.map((step, i) => {
                  const done = i <= activeIdx;
                  const current = i === activeIdx;
                  const Ic = done ? step.icon : Circle;
                  return (
                    <li key={step.key} className="pb-8 pl-10 relative">
                      {i < TIMELINE.length - 1 && (
                        <span className={`absolute left-[13px] top-6 bottom-0 w-0.5 ${done ? "bg-[#8B5A2B]" : "bg-stone-200"}`} />
                      )}
                      <span className={`absolute left-0 top-0 w-7 h-7 flex items-center justify-center rounded-full ${done ? "bg-[#8B5A2B] text-white" : "bg-stone-100 text-stone-400"}`}>
                        <Ic size={16} strokeWidth={done ? 2 : 1.5} />
                      </span>
                      <p className={`font-medium ${done ? "text-stone-900" : "text-stone-400"}`}>{step.label}</p>
                      {current && <p className="text-xs text-[#8B5A2B] mt-1 uppercase tracking-widest">Current</p>}
                    </li>
                  );
                })}
              </ol>
            </div>
          )}

          <div className="mt-6 border-t border-stone-200 pt-6">
            <p className="text-xs tracking-widest uppercase text-stone-500 mb-3">Items ({result.items.length})</p>
            <ul className="text-sm text-stone-700 space-y-1">
              {result.items.map((it, i) => (
                <li key={i}>• {it.name} × {it.quantity}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
